import { useCallback, useEffect, useRef, useState } from 'react';
import { auth } from '@/firebase/firebase';
import { useAuthStore } from '@/stores/authStore';
import {
  createDesktopCheck,
  desktopCheckUrl,
  probeDesktop,
  readDesktopCheck,
  type DesktopCheck,
  type DesktopStatus,
} from '@/services/desktopConnectionService';

type Phase = 'checking' | 'ready' | 'waiting' | 'detected' | 'unknown';

export function useDesktopConnection(enabled: boolean) {
  const uid = useAuthStore((state) => state.currentUser?.uid);
  const [phase, setPhase] = useState<Phase>('checking');
  const [desktop, setDesktop] = useState<DesktopStatus | null>(null);
  const [launchUrl, setLaunchUrl] = useState<string | null>(null);
  const controller = useRef<AbortController | null>(null);
  const check = useRef<DesktopCheck | null>(null);

  const begin = useCallback(async () => {
    controller.current?.abort();
    const operation = new AbortController();
    controller.current = operation;
    setDesktop(null);
    setLaunchUrl(null);
    check.current = null;
    setPhase('checking');
    const limit = window.setTimeout(() => {
      if (!operation.signal.aborted) {
        operation.abort();
        setPhase('unknown');
      }
    }, 15000);
    operation.signal.addEventListener('abort', () => window.clearTimeout(limit), { once: true });
    const local = await probeDesktop(operation.signal);
    if (operation.signal.aborted) return;
    if (local) {
      window.clearTimeout(limit);
      setDesktop(local);
      setPhase('detected');
      return;
    }
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token || auth.currentUser?.uid !== uid) throw new Error('Signed out');
      const created = await createDesktopCheck(token, operation.signal);
      const url = desktopCheckUrl(created);
      if (operation.signal.aborted) return;
      check.current = created;
      setLaunchUrl(url);
      setPhase('ready');
    } catch {
      if (!operation.signal.aborted) setPhase('unknown');
    } finally {
      window.clearTimeout(limit);
    }
  }, [uid]);

  useEffect(() => {
    if (enabled && uid) void begin();
    return () => controller.current?.abort();
  }, [enabled, uid, begin]);

  const openDesktop = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      const current = check.current;
      if (!current || Date.parse(current.expiresAt) - Date.now() < 10000) {
        // Prepare a new link, then let a real link click open the external app.
        // An async API call must not consume the browser's required user gesture.
        event.preventDefault();
        void begin();
        return;
      }
      controller.current?.abort();
      const operation = new AbortController();
      controller.current = operation;
      setPhase('waiting');
      const deadline = Math.min(Date.parse(current.expiresAt), Date.now() + 120000);
      let nextPoll: number | undefined;
      const expiry = window.setTimeout(
        () => {
          if (!operation.signal.aborted) {
            operation.abort();
            setPhase('unknown');
            setLaunchUrl(null);
          }
        },
        Math.max(0, deadline - Date.now()),
      );
      operation.signal.addEventListener(
        'abort',
        () => {
          window.clearTimeout(expiry);
          window.clearTimeout(nextPoll);
        },
        { once: true },
      );
      const poll = async () => {
        try {
          if (operation.signal.aborted) return;
          const user = auth.currentUser;
          if (Date.now() >= deadline || !user || user.uid !== uid) throw new Error('Expired');
          const token = await user.getIdToken();
          const result = await readDesktopCheck(current, token, operation.signal);
          if (operation.signal.aborted) return;
          if (result.status === 'completed' && result.result) {
            setDesktop(result.result);
            setPhase('detected');
            setLaunchUrl(null);
            operation.abort();
          } else if (result.status === 'expired') {
            setPhase('unknown');
            setLaunchUrl(null);
            operation.abort();
          } else {
            nextPoll = window.setTimeout(() => {
              void poll();
            }, 1500);
          }
        } catch {
          if (!operation.signal.aborted) {
            setPhase('unknown');
            setLaunchUrl(null);
            operation.abort();
          }
        }
      };
      void poll();
    },
    [begin, uid],
  );

  return { phase, desktop, launchUrl, openDesktop, recheck: begin };
}
